import { CARD_PAYMENTS_ENABLED, SBP_MIN_AMOUNT_RUBLES } from 'modules/organizer/domain/constants/wallet-topup';
import { TournamentPlan } from 'modules/organizer/domain/interfaces/tournament-plan';
import { WalletTopupPaymentMethod } from 'modules/organizer/domain/interfaces/wallet-topup';
import { formatRublesFromMinorUnits } from 'modules/organizer/domain/utils/money';
import { formatSubscriptionPeriod } from 'modules/organizer/domain/utils/subscription-period';
import {
  walletTopupFormValidator,
  WalletTopupFormValues,
} from 'modules/organizer/domain/validators/wallet-topup.validator';
import React from 'react';
import { Form, Formik } from 'formik';
import UDFormInput from 'ud-form/components/input';
import UDButton from 'ud-ui/components/button';
import UDCard from 'ud-ui/components/card';
import UDText from 'ud-ui/components/text';
import { theme } from 'styles/theme/theme';

interface SubscriptionPlanPickerProps {
  plans: TournamentPlan[];
  selectedPlanId: number;
  onSelect: (planId: number) => void;
  locked: boolean;
}

export const SubscriptionPlanPicker: React.FC<SubscriptionPlanPickerProps> = ({ plans, selectedPlanId, onSelect, locked }) => {
  if (plans.length === 0) {
    return (
      <UDText type="body-text" color={theme.colors.Gray}>
        Тарифы пока недоступны.
      </UDText>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {plans.map((plan) => {
        const selected = plan.id === selectedPlanId;

        return (
          <UDCard
            key={plan.id}
            className={selected ? 'border-darkBlue-400' : undefined}
            onClick={locked ? undefined : () => onSelect(plan.id)}
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <UDText type="subhead">{plan.name}</UDText>
                <UDText type="footnote" color={theme.colors.Gray}>
                  {formatRublesFromMinorUnits(plan.priceInMinorUnits)} ₽ / {formatSubscriptionPeriod(plan.period)}
                </UDText>
              </div>
              {selected && (
                <UDText type="footnote" color={theme.colors.Gray}>
                  Выбран
                </UDText>
              )}
            </div>
          </UDCard>
        );
      })}
    </div>
  );
};

interface SubscriptionTopupFormProps {
  initialAmount: number;
  initialEmail: string;
  isSubmitting: boolean;
  error: string | null;
  onSubmit: (values: WalletTopupFormValues) => Promise<void>;
}

export const SubscriptionTopupForm: React.FC<SubscriptionTopupFormProps> = ({
  initialAmount,
  initialEmail,
  isSubmitting,
  error,
  onSubmit,
}) => {
  return (
    <Formik<WalletTopupFormValues>
      initialValues={{ amount: initialAmount, email: initialEmail, paymentMethod: 'SBP' }}
      enableReinitialize
      validationSchema={walletTopupFormValidator}
      onSubmit={onSubmit}
    >
      {({ values, isSubmitting: formSubmitting, validateForm, setTouched, setSubmitting }) => {
        const pay = async (paymentMethod: WalletTopupPaymentMethod) => {
          const errors = await validateForm({ ...values, paymentMethod });
          if (Object.keys(errors).length > 0) {
            await setTouched({ amount: true, email: true, paymentMethod: true });
            return;
          }

          setSubmitting(true);
          try {
            await onSubmit({ amount: values.amount, email: values.email.trim(), paymentMethod });
          } finally {
            setSubmitting(false);
          }
        };

        const busy = isSubmitting || formSubmitting;

        return (
          <Form className="flex flex-col gap-4">
            <UDText type="headline">Пополнить баланс</UDText>
            <UDText type="body-text" color={theme.colors.Gray}>
              Оплата по СБП. После зачисления деньги остаются на кошельке — тариф подключается отдельно.
            </UDText>
            <UDFormInput name="amount" type="number" label="Сумма, ₽" min={SBP_MIN_AMOUNT_RUBLES} step="1" />
            <UDFormInput name="email" type="email" label="Email для чека" />
            {error && (
              <UDText type="footnote" color={theme.colors.CaseErrorText}>
                {error}
              </UDText>
            )}
            <div className="flex flex-col gap-2">
              <UDButton type="button" variant="primary" loading={busy} onClick={() => void pay('SBP')}>
                Пополнить по СБП
              </UDButton>
              {CARD_PAYMENTS_ENABLED && (
                <UDButton type="button" variant="secondary" loading={busy} onClick={() => void pay('CARD')}>
                  Оплатить картой
                </UDButton>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};
